import React, { useState, useEffect, useRef } from 'react';
import Header from '../../components/admin/Header';
import Sidebar from '../../components/admin/Sidebar';
import Footer from '../../components/admin/Footer';
import { Editor } from '@tinymce/tinymce-react';
import { 
  FileText,
  Trash2,
  Image as ImageIcon,
  X,
  Eye,
  Plus,
  AlertCircle,
  Calendar,
  Edit,
  Save,
  List
} from 'lucide-react';
import axios from 'axios';

const UploadBlog = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [blogs, setBlogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const editorRef = useRef(null);
  
  // Form states
  const [formData, setFormData] = useState({
    id: null,
    title: '',
    category: '',
    content: '',
    image: null
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [previewModal, setPreviewModal] = useState({
    isOpen: false,
    blog: null
  });

  // Fetch blogs
  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const res = await axios.get('https://api.marketmaestroovip.in/api/blogs');
      setBlogs(res.data);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      setBlogs([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleContentChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        image: file
      }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const resetForm = () => {
    setFormData({
      id: null,
      title: '',
      category: '',
      content: '',
      image: null
    });
    setImagePreview(null);
    setIsEditing(false);
    setMessage({ type: '', text: '' });
    
    // Clear file input
    const fileInput = document.getElementById('blog-image');
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      setMessage({ type: 'error', text: 'Title and content are required' });
      return;
    }

    const submitData = new FormData();
    submitData.append('title', formData.title);
    submitData.append('category', formData.category || '');
    submitData.append('content', formData.content);
    
    if (formData.image) {
      submitData.append('image', formData.image);
    }

    try {
      if (isEditing && formData.id) {
        // Update existing blog
        await axios.put(
          `https://api.marketmaestroovip.in/api/blogs/${formData.id}`,
          submitData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setMessage({ type: 'success', text: 'Blog updated successfully!' });
      } else {
        // Create new blog
        await axios.post(
          'https://api.marketmaestroovip.in/api/blogs',
          submitData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setMessage({ type: 'success', text: 'Blog created successfully!' });
      }
      
      resetForm();
      fetchBlogs();
      
    } catch (error) {
      console.error('Submit error:', error);
      setMessage({ type: 'error', text: 'Operation failed. Please try again.' });
    }
  };

  const handleEdit = (blog) => {
    setFormData({
      id: blog.id,
      title: blog.title,
      category: blog.category || '',
      content: blog.content,
      image: null
    });
    setImagePreview(blog.image ? `https://api.marketmaestroovip.in/${blog.image}` : null);
    setIsEditing(true);
    setMessage({ type: '', text: '' });
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog?')) return;

    try {
      await axios.delete(`https://api.marketmaestroovip.in/api/blogs/${id}`);
      fetchBlogs();
      setMessage({ type: 'success', text: 'Blog deleted successfully!' });
    } catch (error) {
      console.error('Delete error:', error);
      setMessage({ type: 'error', text: 'Delete failed. Please try again.' });
    }
  };

  const openPreviewModal = (blog) => {
    setPreviewModal({
      isOpen: true,
      blog
    });
  };

  const closePreviewModal = () => {
    setPreviewModal({
      isOpen: false,
      blog: null
    });
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image: null
    }));
    setImagePreview(null);
    const fileInput = document.getElementById('blog-image');
    if (fileInput) fileInput.value = '';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const stripHtmlTags = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} />
        
        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-h-screen overflow-hidden lg:ml-64">
          {/* Header */}
          <Header toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

          {/* Main Content */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white">
                Blog Management
              </h1>
              <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1 md:mt-2">
                Create and manage your blog posts
              </p>
            </div>

            {/* Blog Form Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6 mb-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4 flex items-center gap-2">
                {isEditing ? <Edit className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
                {isEditing ? 'Edit Blog Post' : 'Create New Blog Post'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                    placeholder="Enter blog title"
                    required
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Category
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700"
                    placeholder="e.g., Technology, News, Tutorial"
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Featured Image
                  </label>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4">
                    <input
                      type="file"
                      id="blog-image"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                    
                    {imagePreview ? (
                      <div className="relative">
                        <img 
                          src={imagePreview} 
                          alt="Preview" 
                          className="max-h-48 mx-auto object-contain rounded"
                        />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <label htmlFor="blog-image" className="cursor-pointer block">
                        <div className="flex flex-col items-center py-4">
                          <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            Click to upload featured image
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            PNG, JPG, JPEG up to 5MB
                          </p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Content Editor - TinyMCE */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Content <span className="text-red-500">*</span>
                  </label>
                  <div className="bg-white dark:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600">
                    <Editor
                      apiKey='lh13oufgfz8yn7cqv51xszjtqzt9smo8ul69wqv98ju5apzk' // Replace with your actual API key from tiny.cloud
                      onInit={(evt, editor) => editorRef.current = editor}
                      value={formData.content}
                      onEditorChange={handleContentChange}
                      init={{
                        height: 500,
                        menubar: true,
                        plugins: [
                          'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                          'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                          'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                        ],
                        toolbar: 'undo redo | blocks | ' +
                          'bold italic forecolor | alignleft aligncenter ' +
                          'alignright alignjustify | bullist numlist outdent indent | ' +
                          'removeformat | help',
                        content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                        branding: false,
                        promotion: false,
                        setup: (editor) => {
                          editor.on('change', () => {
                            handleContentChange(editor.getContent());
                          });
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex items-center gap-4 pt-4">
                  <button
                    type="submit"
                    className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium flex items-center gap-2 transition"
                  >
                    <Save className="w-4 h-4" />
                    {isEditing ? 'Update Blog' : 'Publish Blog'}
                  </button>
                  
                  {isEditing && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                    >
                      Cancel
                    </button>
                  )}
                </div>

                {/* Message */}
                {message.text && (
                  <div className={`p-3 rounded-lg flex items-center gap-2 ${
                    message.type === 'success' 
                      ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                      : 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  }`}>
                    <AlertCircle className="w-5 h-5" />
                    <span>{message.text}</span>
                  </div>
                )}
              </form>
            </div>

            {/* Blogs List Section */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 md:p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  All Blog Posts
                  <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                    ({blogs.length})
                  </span>
                </h2>
                <button
                  onClick={fetchBlogs}
                  className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-lg flex items-center gap-2"
                >
                  <span className="rotate-90">↻</span>
                  Refresh
                </button>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : blogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                  <h3 className="text-lg font-medium mb-2">No blog posts yet</h3>
                  <p className="text-gray-500 dark:text-gray-400">
                    Create your first blog post using the form above
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Image
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Title
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {blogs.map((blog) => (
                        <tr key={blog.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-4">
                            {blog.image ? (
                              <img 
                                src={`https://api.marketmaestroovip.in/${blog.image}`}
                                alt={blog.title}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
                                <ImageIcon className="w-6 h-6 text-gray-400" />
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="font-medium">{blog.title}</div>
                            <div className="text-xs text-gray-500 mt-1">
                              {stripHtmlTags(blog.content).substring(0, 60)}...
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {blog.category ? (
                              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs">
                                {blog.category}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-400" />
                              <span className="text-sm">{formatDate(blog.created_at)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openPreviewModal(blog)}
                                className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-lg transition"
                                title="Preview"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(blog)}
                                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900 rounded-lg transition"
                                title="Edit"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(blog.id)}
                                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>

          {/* Footer */}
          <Footer />
        </div>
      </div>

      {/* Preview Modal */}
      {previewModal.isOpen && previewModal.blog && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold">{previewModal.blog.title}</h3>
              <button
                onClick={closePreviewModal}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {previewModal.blog.image && (
                <img 
                  src={`https://api.marketmaestroovip.in/${previewModal.blog.image}`}
                  alt={previewModal.blog.title}
                  className="w-full max-h-64 object-cover rounded-lg mb-4"
                />
              )}
              {previewModal.blog.category && (
                <span className="inline-block px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded-full text-xs mb-4">
                  {previewModal.blog.category}
                </span>
              )}
              <div 
                className="prose dark:prose-invert max-w-none"
                dangerouslySetInnerHTML={{ __html: previewModal.blog.content }}
              />
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={closePreviewModal}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadBlog;